import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../translations';
import {
  PanelContainer, Title, Subtitle, PageHeader, TitleRow, IconButton, PanelCard, SectionTitle, ToggleSwitch,
  TextInput, PrimaryButton, SecondaryButton,
} from '../ui/common';
import { ArrowLeftIcon } from '../ui/icons';
import { Banner } from '../components/ui/Banner';
import { TylkoOdczytWPrzerwie } from '../components/przerwa/KontoWPrzerwie';
import { bladPrzerwy } from '../utils/kontoWPrzerwie';
import { parseAmount } from '../utils/amountField';
import { formatMoney } from '../utils/format';
import { WIERSZE_TABELI, dniPlatne, pokazDniPlatne } from '../utils/dniPlatne';
import { API_BASE } from '../config';

// Zasady wynajmu firmy (FAZA N): reguły obowiązujące przy każdej rezerwacji, w panelu i w widgecie.
// Serwer zapisuje tylko klucze obecne w ciele (PUT /api/rental-rules, lib/zasadyWynajmu.js), więc
// każda sekcja wysyła wyłącznie swoją zmianę. Przełącznik wymiany zapisuje od razu, jak Szybkie
// reguły w cennikach: nie ma tam czego zatwierdzać drugim kliknięciem. Tabela dni płatnych (N.3) to
// 14 pól, więc ma własny przycisk zapisu, a wyłączenie własnej tabeli też zapisuje od razu.
//
// Pola tabeli trzymają SUROWY TEKST (jak pola kwot, utils/amountField.js), bo przecinek i kropka mają
// przechodzić, a pustego pola nie wolno zamieniać na zero w trakcie pisania. Tu odczytujemy tylko
// liczbę: reguły tabeli (zakres, kolejność, miejsca po przecinku) zna serwer i zwraca numer wiersza.
const ADRES = `${API_BASE}/api/rental-rules`;
// Przykład obok wiersza: okrągła stawka, z której łatwo czytać rabat.
const STAWKA_PRZYKLADU = 100;

const tekstyTabeli = (tabela) => Object.fromEntries(WIERSZE_TABELI.map((n) => [n, pokazDniPlatne(dniPlatne(n, tabela))]));
const bezRabatu = () => Object.fromEntries(WIERSZE_TABELI.map((n) => [n, String(n)]));

export default function RentalRules() {
  const navigate = useNavigate();
  const { t, currentLanguage } = useTranslation();
  const [zasady, setZasady] = useState(null);
  const [bladOdczytu, setBladOdczytu] = useState(false);
  const [zapisywanie, setZapisywanie] = useState(false);
  const [bladZapisu, setBladZapisu] = useState('');
  // Tabela otwarta, ale jeszcze niezapisana: włącznik działa od razu, a tabela zaczyna obowiązywać
  // dopiero po „Zapisz tabelę”.
  const [wlasnaTabela, setWlasnaTabela] = useState(false);
  const [wiersze, setWiersze] = useState(() => tekstyTabeli(null));
  const [bladWiersza, setBladWiersza] = useState(null);
  const [tabelaZapisana, setTabelaZapisana] = useState(false);
  const administrator = localStorage.getItem('isAdmin') === '1' || localStorage.getItem('isSuperAdmin') === '1';

  const pokazTabele = useCallback((dane) => {
    setWlasnaTabela(!!dane.paid_days);
    setWiersze(tekstyTabeli(dane.paid_days || null));
    setBladWiersza(null);
  }, []);

  const wczytaj = useCallback(async () => {
    setBladOdczytu(false);
    try {
      const res = await fetch(ADRES, { credentials: 'include' });
      if (!res.ok) throw new Error(String(res.status));
      const dane = await res.json();
      setZasady(dane);
      pokazTabele(dane);
    } catch (_) {
      setBladOdczytu(true);
    }
  }, [pokazTabele]);

  useEffect(() => { wczytaj(); }, [wczytaj]);

  // Fokus na polu z błędem przewija do niego: tabela jest długa, a przycisk zapisu stoi pod nią.
  useEffect(() => {
    if (bladWiersza) document.getElementById(`rental-rules-paid-days-input-${bladWiersza.wiersz}`)?.focus();
  }, [bladWiersza]);

  const zapisz = async (zmiany) => {
    setZapisywanie(true);
    setBladZapisu('');
    setTabelaZapisana(false);
    try {
      const res = await fetch(ADRES, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': localStorage.getItem('csrfToken') || '' },
        body: JSON.stringify(zmiany),
      });
      const dane = await res.json().catch(() => ({}));
      if (res.ok) {
        setZasady(dane);
        return dane;
      }
      if (dane.code === 'paid_days_invalid' && dane.row) {
        setBladWiersza({ wiersz: dane.row, powod: dane.reason });
      } else if (!bladPrzerwy(dane)) {
        // Odmowę trybu tylko do odczytu pokazuje już baner i komunikat całego panelu.
        setBladZapisu(t('rentalRules.saveError'));
      }
    } catch (_) {
      setBladZapisu(t('rentalRules.saveError'));
    } finally {
      setZapisywanie(false);
    }
    return null;
  };

  const zapiszTabele = async () => {
    const pusty = WIERSZE_TABELI.find((n) => parseAmount(wiersze[n]) === null);
    if (pusty) {
      setBladWiersza({ wiersz: pusty, powod: 'number' });
      return;
    }
    setBladWiersza(null);
    const dane = await zapisz({ paid_days: Object.fromEntries(WIERSZE_TABELI.map((n) => [n, parseAmount(wiersze[n])])) });
    if (dane) {
      pokazTabele(dane);
      setTabelaZapisana(true);
    }
  };

  const przywrocDomyslne = async () => {
    const dane = await zapisz({ paid_days: null });
    if (dane) pokazTabele(dane);
  };

  const przelaczTabele = () => {
    if (!wlasnaTabela) {
      setWlasnaTabela(true);
    } else if (zasady.paid_days) {
      przywrocDomyslne();
    } else {
      pokazTabele(zasady);
    }
  };

  const zmienWiersz = (n, tekst) => {
    setWiersze((obecne) => ({ ...obecne, [n]: tekst }));
    setTabelaZapisana(false);
    if (bladWiersza && bladWiersza.wiersz === n) setBladWiersza(null);
  };

  const wypelnijBezRabatu = () => {
    setWiersze(bezRabatu());
    setBladWiersza(null);
    setTabelaZapisana(false);
  };

  const komunikatWiersza = ({ wiersz, powod }) => {
    if (powod === 'range') return t('rentalRules.paidDays.errorRange', { n: wiersz });
    if (powod === 'decimals') return t('rentalRules.paidDays.errorDecimals');
    if (powod === 'order') return t('rentalRules.paidDays.errorOrder', { row: wiersz - 1 });
    return t('rentalRules.paidDays.errorNumber');
  };

  const wymiana = !!zasady?.same_day_exchange;
  const zablokowane = !administrator || zapisywanie;

  return (
    <PanelContainer>
      <PageHeader>
        <div>
          <TitleRow>
            <IconButton type="button" onClick={() => navigate('/profile')} aria-label={t('rentalRules.back')} title={t('rentalRules.back')}>
              <ArrowLeftIcon size={20} />
            </IconButton>
            <Title>{t('rentalRules.title')}</Title>
          </TitleRow>
          <Subtitle>{t('rentalRules.subtitle')}</Subtitle>
        </div>
      </PageHeader>

      {bladOdczytu && (
        <Banner tone="danger" action={{ label: t('common.retry'), onClick: wczytaj }} data-cy="rental-rules-load-error">
          {t('rentalRules.loadError')}
        </Banner>
      )}

      {zasady && (
        <TylkoOdczytWPrzerwie data-cy="rental-rules-form">
          {!administrator && (
            <Odstep>
              <Banner tone="info" compact data-cy="rental-rules-admin-only">{t('rentalRules.adminOnly')}</Banner>
            </Odstep>
          )}
          {bladZapisu && (
            <Odstep>
              <Banner tone="danger" compact data-cy="rental-rules-save-error">{bladZapisu}</Banner>
            </Odstep>
          )}

          <PanelCard data-cy="rental-rules-exchange">
            <Wiersz>
              <div>
                <NaglowekSekcji id="rental-rules-exchange-title">{t('rentalRules.exchange.title')}</NaglowekSekcji>
                <Opis id="rental-rules-exchange-description">{t('rentalRules.exchange.description')}</Opis>
                {wymiana && (
                  <Uwaga id="rental-rules-exchange-off-note" data-cy="rental-rules-exchange-off-note">
                    {t('rentalRules.exchange.offNote')}
                  </Uwaga>
                )}
              </div>
              <Przelacznik>
                <ToggleSwitch
                  type="button"
                  role="switch"
                  aria-checked={wymiana}
                  aria-labelledby="rental-rules-exchange-title"
                  aria-describedby={wymiana ? 'rental-rules-exchange-description rental-rules-exchange-off-note' : 'rental-rules-exchange-description'}
                  data-cy="rental-rules-exchange-toggle"
                  disabled={!administrator || zapisywanie}
                  $on={wymiana}
                  onClick={() => zapisz({ same_day_exchange: !wymiana })}
                />
                <span data-cy="rental-rules-exchange-state">{wymiana ? t('rentalRules.on') : t('rentalRules.off')}</span>
              </Przelacznik>
            </Wiersz>
          </PanelCard>

          <PanelCard data-cy="rental-rules-paid-days">
            <Wiersz>
              <div>
                <NaglowekSekcji id="rental-rules-paid-days-title">{t('rentalRules.paidDays.title')}</NaglowekSekcji>
                <Opis id="rental-rules-paid-days-description">{t('rentalRules.paidDays.description')}</Opis>
                {!wlasnaTabela && (
                  <Uwaga data-cy="rental-rules-paid-days-default">{t('rentalRules.paidDays.defaultRule')}</Uwaga>
                )}
              </div>
              <Przelacznik>
                <ToggleSwitch
                  type="button"
                  role="switch"
                  aria-checked={wlasnaTabela}
                  aria-labelledby="rental-rules-paid-days-own"
                  aria-describedby="rental-rules-paid-days-description"
                  data-cy="rental-rules-paid-days-toggle"
                  disabled={zablokowane}
                  $on={wlasnaTabela}
                  onClick={przelaczTabele}
                />
                <span id="rental-rules-paid-days-own">{t('rentalRules.paidDays.own')}</span>
              </Przelacznik>
            </Wiersz>

            {wlasnaTabela && (
              <>
                {!zasady.paid_days && (
                  <OdstepNad>
                    <Banner tone="info" compact data-cy="rental-rules-paid-days-unsaved">{t('rentalRules.paidDays.unsaved')}</Banner>
                  </OdstepNad>
                )}
                <TabelaDni data-cy="rental-rules-paid-days-table">
                  <thead>
                    <tr>
                      <th scope="col">{t('rentalRules.paidDays.colRental')}</th>
                      <th scope="col">{t('rentalRules.paidDays.colPaid')}</th>
                      <th scope="col">{t('rentalRules.paidDays.colExample', { rate: STAWKA_PRZYKLADU })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {WIERSZE_TABELI.map((n) => {
                      const wartosc = parseAmount(wiersze[n]);
                      const blad = bladWiersza && bladWiersza.wiersz === n ? komunikatWiersza(bladWiersza) : '';
                      return (
                        <tr key={n} data-cy={`rental-rules-paid-days-row-${n}`}>
                          <th scope="row">{n === 1 ? t('rentalRules.paidDays.rowOne') : t('rentalRules.paidDays.rowMany', { n })}</th>
                          <td>
                            <PoleDni
                              id={`rental-rules-paid-days-input-${n}`}
                              data-cy={`rental-rules-paid-days-input-${n}`}
                              type="text"
                              inputMode="decimal"
                              autoComplete="off"
                              aria-label={t('rentalRules.paidDays.inputLabel', { n })}
                              aria-invalid={blad ? 'true' : undefined}
                              aria-describedby={blad ? `rental-rules-paid-days-error-${n}` : undefined}
                              data-invalid={blad ? 'true' : undefined}
                              disabled={zablokowane}
                              value={wiersze[n]}
                              onChange={(e) => zmienWiersz(n, e.target.value)}
                            />
                            {blad && (
                              <BladWiersza id={`rental-rules-paid-days-error-${n}`} role="alert" data-cy={`rental-rules-paid-days-error-${n}`}>{blad}</BladWiersza>
                            )}
                          </td>
                          <td data-cy={`rental-rules-paid-days-example-${n}`}>
                            {wartosc === null ? '' : formatMoney(wartosc * STAWKA_PRZYKLADU, { locale: currentLanguage })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </TabelaDni>
                <Uwaga>{t('rentalRules.paidDays.over14')}</Uwaga>
                <Uwaga>{t('rentalRules.paidDays.over7Rate')}</Uwaga>
                <Uwaga>{t('rentalRules.paidDays.savedReservations')}</Uwaga>
                <Akcje>
                  <PrimaryButton type="button" data-cy="rental-rules-paid-days-save" disabled={zablokowane} onClick={zapiszTabele}>
                    {t('rentalRules.paidDays.save')}
                  </PrimaryButton>
                  <SecondaryButton type="button" data-cy="rental-rules-paid-days-no-discount" disabled={zablokowane} onClick={wypelnijBezRabatu}>
                    {t('rentalRules.paidDays.noDiscount')}
                  </SecondaryButton>
                  <SecondaryButton type="button" data-cy="rental-rules-paid-days-reset" disabled={zablokowane} onClick={przywrocDomyslne}>
                    {t('rentalRules.paidDays.reset')}
                  </SecondaryButton>
                </Akcje>
                {tabelaZapisana && (
                  <Uwaga role="status" data-cy="rental-rules-paid-days-saved">{t('rentalRules.paidDays.saved')}</Uwaga>
                )}
              </>
            )}
          </PanelCard>
        </TylkoOdczytWPrzerwie>
      )}
    </PanelContainer>
  );
}

const Odstep = styled.div`
  margin-bottom: var(--sp-4);
`;

const OdstepNad = styled.div`
  margin-top: var(--sp-4);
`;

const Wiersz = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--sp-5);

  @media (max-width: 560px) {
    flex-direction: column;
    gap: var(--sp-3);
  }
`;

const NaglowekSekcji = styled(SectionTitle)`
  margin-bottom: var(--sp-2);
`;

const Opis = styled.p`
  margin: 0;
  max-width: 62ch;
  color: var(--text-muted);
  font-size: var(--fs-sm);
  line-height: 1.5;
`;

const Uwaga = styled(Opis)`
  margin-top: var(--sp-2);
`;

const Przelacznik = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-shrink: 0;
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--text);
`;

const TabelaDni = styled.table`
  width: 100%;
  margin-top: var(--sp-4);
  border-collapse: collapse;
  font-size: var(--fs-sm);
  color: var(--text);

  th, td {
    padding: var(--sp-2) var(--sp-3) var(--sp-2) 0;
    border-bottom: 1px solid var(--border);
    text-align: left;
    vertical-align: top;
  }

  thead th {
    color: var(--text-muted);
    font-weight: 600;
  }

  tbody th {
    font-weight: 500;
    white-space: nowrap;
    padding-top: calc(var(--sp-2) + 10px);
  }

  tbody td:last-child {
    color: var(--text-muted);
    padding-top: calc(var(--sp-2) + 10px);
    white-space: nowrap;
  }

  td:last-child, th:last-child {
    padding-right: 0;
  }
`;

const PoleDni = styled(TextInput)`
  max-width: 96px;
  padding: var(--sp-2) var(--sp-3);

  &[data-invalid='true'] {
    border-color: var(--danger);
  }
`;

const BladWiersza = styled.div`
  margin-top: var(--sp-1);
  color: var(--danger);
  font-size: var(--fs-xs);
`;

const Akcje = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-3);
  margin-top: var(--sp-4);
`;
